import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import AdminShell, { fieldSx, panelSx, primaryButtonSx, mutedTextSx, sectionTitleSx } from "./AdminShell";
import { getSession } from "../../api/session";
import { slugify } from "../utils/slug";
import {
  adminListAllMissions,
  fetchAllLaunchOptions,
  fetchMissionLaunchAssignments,
  adminUpsertLaunchOption,
  adminDeleteLaunchOption,
  adminSetLaunchOptionMission,
  adminSetLaunchOptionCredit,
  adminSetLaunchOptionTle,
  adminSetLaunchOptionYoutube,
  uploadLaunchOptionImage,
  uploadLaunchVideo,
} from "../../api/queries";
import type { AdminMissionRow } from "../../types/admin";
import type { LaunchOptionRow } from "../../types/launch";
import { formatClipTime, parseClipTime, youTubeId } from "../uplink/youtube";
import { DEFAULT_POWERED_S } from "../uplink/ascent";

// Numbers are edited as text, so half-typed values ("97.") stay as typed.
type LaunchForm = {
  id: string | null;
  name: string;
  vehicle: string;
  provider: string;
  summary: string;
  launchSite: string;
  siteLat: string;
  siteLon: string;
  altitudeKm: string;
  inclinationDeg: string;
  eccentricity: string;
  priceText: string;
  imageUrl: string;
  imageCredit: string;
  videoUrl: string;
  // YouTube launch video and the part to play, times as typed ("29:41").
  youtubeUrl: string;
  youtubeStart: string;
  youtubeEnd: string;
  orderIndex: number;
  missionIds: string[];
  // Optional TLE details; empty = the app picks a realistic value.
  tleIntlDesignator: string;
  tleCatalogBase: string;
  tleRaanDeg: string;
  tleArgPerigeeDeg: string;
  tleMeanAnomalyDeg: string;
  tleBstar: string;
  tleMeanMotionDot: string;
};

const EMPTY_FORM: LaunchForm = {
  id: null,
  name: "",
  vehicle: "",
  provider: "",
  summary: "",
  launchSite: "",
  siteLat: "",
  siteLon: "",
  altitudeKm: "",
  inclinationDeg: "",
  eccentricity: "0.001",
  priceText: "",
  imageUrl: "",
  imageCredit: "",
  videoUrl: "",
  youtubeUrl: "",
  youtubeStart: "",
  youtubeEnd: "",
  orderIndex: 0,
  missionIds: [],
  tleIntlDesignator: "",
  tleCatalogBase: "",
  tleRaanDeg: "",
  tleArgPerigeeDeg: "",
  tleMeanAnomalyDeg: "",
  tleBstar: "",
  tleMeanMotionDot: "",
};

const textButtonSx = { textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "rgba(226,232,240,0.7)" };
const uploadButtonSx = { textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD", border: "1px solid #4C3A70", borderRadius: 1.5, px: 1.25 };

const toNumber = (text: string) => (text.trim() === "" ? null : Number(text.trim().replace(",", ".")));
const toText = (value: number | null | undefined) => (value === null || value === undefined ? "" : String(value));

// Launches tab: the ways to orbit that missions offer on their sheet (three
// per mission is the idea), each with the orbit it delivers to -- the
// groups' TLEs are built from it -- and the video played at liftoff.
export default function AdminLaunches(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const session = React.useMemo(() => getSession(), []);
  const email = session?.email ?? "";

  const [options, setOptions] = React.useState<LaunchOptionRow[]>([]);
  const [missions, setMissions] = React.useState<AdminMissionRow[]>([]);
  const [assignments, setAssignments] = React.useState<{ mission_id: string; option_id: string }[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<LaunchForm | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState<"image" | "video" | null>(null);

  const load = React.useCallback(async () => {
    const [{ data: o, error: oErr }, { data: m, error: mErr }, { data: a, error: aErr }] = await Promise.all([
      fetchAllLaunchOptions(),
      adminListAllMissions(email),
      fetchMissionLaunchAssignments(),
    ]);
    setLoading(false);
    if (oErr || mErr || aErr) {
      setError(
        `${oErr?.message ?? mErr?.message ?? aErr?.message ?? "Failed to load."} If this is the first time, run supabase/sql/launch.sql.`
      );
      return;
    }
    setOptions(o ?? []);
    setMissions(m ?? []);
    setAssignments(a ?? []);
  }, [email]);

  React.useEffect(() => {
    load();
  }, [load]);

  const missionIdsOf = (optionId: string) => assignments.filter((a) => a.option_id === optionId).map((a) => a.mission_id);

  const edit = (o: LaunchOptionRow) =>
    setForm({
      id: o.id,
      name: o.name,
      vehicle: o.vehicle,
      provider: o.provider,
      summary: o.summary,
      launchSite: o.launch_site,
      siteLat: o.launch_site_lat === null ? "" : String(o.launch_site_lat),
      siteLon: o.launch_site_lon === null ? "" : String(o.launch_site_lon),
      altitudeKm: String(o.orbit_altitude_km),
      inclinationDeg: String(o.orbit_inclination_deg),
      eccentricity: String(o.orbit_eccentricity),
      priceText: o.price_text,
      imageUrl: o.image_url ?? "",
      imageCredit: o.image_credit ?? "",
      videoUrl: o.video_url ?? "",
      youtubeUrl: o.video_youtube_url ?? "",
      youtubeStart: o.video_start_s === null ? "" : formatClipTime(o.video_start_s),
      youtubeEnd: o.video_end_s === null ? "" : formatClipTime(o.video_end_s),
      orderIndex: o.order_index,
      missionIds: missionIdsOf(o.id),
      tleIntlDesignator: o.tle_intl_designator ?? "",
      tleCatalogBase: toText(o.tle_catalog_base),
      tleRaanDeg: toText(o.tle_raan_deg),
      tleArgPerigeeDeg: toText(o.tle_arg_perigee_deg),
      tleMeanAnomalyDeg: toText(o.tle_mean_anomaly_deg),
      tleBstar: toText(o.tle_bstar),
      tleMeanMotionDot: toText(o.tle_mean_motion_dot),
    });

  const save = async () => {
    if (!form) return;
    const altitude = toNumber(form.altitudeKm);
    const inclination = toNumber(form.inclinationDeg);
    const eccentricity = toNumber(form.eccentricity) ?? 0;
    const lat = toNumber(form.siteLat);
    const lon = toNumber(form.siteLon);
    if (!form.name.trim()) return setError("Give the launch option a name.");
    if (altitude === null || inclination === null || [altitude, inclination, eccentricity, lat ?? 0, lon ?? 0].some((v) => !Number.isFinite(v))) {
      return setError("Altitude, inclination, eccentricity and the launch site coordinates must be numbers.");
    }
    const tle = {
      tle_intl_designator: form.tleIntlDesignator.trim(),
      tle_catalog_base: toNumber(form.tleCatalogBase),
      tle_raan_deg: toNumber(form.tleRaanDeg),
      tle_arg_perigee_deg: toNumber(form.tleArgPerigeeDeg),
      tle_mean_anomaly_deg: toNumber(form.tleMeanAnomalyDeg),
      tle_bstar: toNumber(form.tleBstar),
      tle_mean_motion_dot: toNumber(form.tleMeanMotionDot),
    };
    if (tle.tle_intl_designator && !/^\d{5}$/.test(tle.tle_intl_designator)) {
      return setError("The international designator must be 5 digits: year and launch number, e.g. 26045.");
    }
    if (tle.tle_catalog_base !== null && !Number.isInteger(tle.tle_catalog_base)) {
      return setError("The catalog number must be a whole number.");
    }
    if (Object.values(tle).some((v) => typeof v === "number" && !Number.isFinite(v))) {
      return setError("The TLE details must be numbers (or empty).");
    }

    const youtube = {
      url: form.youtubeUrl.trim(),
      startS: form.youtubeStart.trim() ? parseClipTime(form.youtubeStart) : null,
      endS: form.youtubeEnd.trim() ? parseClipTime(form.youtubeEnd) : null,
    };
    if (youtube.url && !youTubeId(youtube.url)) {
      return setError("That doesn't look like a YouTube video link (e.g. https://www.youtube.com/watch?v=AT_7CY5brRo).");
    }
    if ((form.youtubeStart.trim() && youtube.startS === null) || (form.youtubeEnd.trim() && youtube.endS === null)) {
      return setError("Start and end must be times like 29:41 (minutes:seconds) or 1:02:03.");
    }
    if (youtube.startS !== null && youtube.endS !== null && youtube.endS <= youtube.startS) {
      return setError("\"Orbit reached at\" must come after \"Play from\".");
    }

    setSaving(true);
    setError(null);
    const { data: saved, error: saveError } = await adminUpsertLaunchOption(email, {
      id: form.id,
      name: form.name.trim(),
      vehicle: form.vehicle.trim(),
      provider: form.provider.trim(),
      summary: form.summary.trim(),
      launch_site: form.launchSite.trim(),
      launch_site_lat: lat,
      launch_site_lon: lon,
      orbit_altitude_km: altitude,
      orbit_inclination_deg: inclination,
      orbit_eccentricity: eccentricity,
      price_text: form.priceText.trim(),
      image_url: form.imageUrl.trim() || null,
      video_url: form.videoUrl.trim() || null,
      order_index: form.orderIndex,
    });
    if (saveError || !saved) {
      setSaving(false);
      setError(saveError?.message ?? "Couldn't save the launch option.");
      return;
    }

    // Credit, TLE details and YouTube video are stored separately
    // (launch_images.sql, tle_details.sql, launch_youtube.sql).
    const [{ error: creditError }, { error: tleError }, { error: youtubeError }] = await Promise.all([
      adminSetLaunchOptionCredit(email, saved.id, form.imageCredit.trim()),
      adminSetLaunchOptionTle(email, saved.id, tle),
      adminSetLaunchOptionYoutube(email, saved.id, youtube),
    ]);
    if (creditError || tleError || youtubeError) {
      setSaving(false);
      setError((creditError ?? tleError ?? youtubeError)?.message ?? "Couldn't save the launch option.");
      load();
      return;
    }

    const before = form.id ? missionIdsOf(form.id) : [];
    const changes = [
      ...form.missionIds.filter((id) => !before.includes(id)).map((id) => ({ id, assigned: true })),
      ...before.filter((id) => !form.missionIds.includes(id)).map((id) => ({ id, assigned: false })),
    ];
    for (const change of changes) {
      const { error: assignError } = await adminSetLaunchOptionMission(email, saved.id, change.id, change.assigned);
      if (assignError) {
        setSaving(false);
        setError(assignError.message);
        load();
        return;
      }
    }

    setSaving(false);
    // A new option stays open, now with its id, so its video can be uploaded.
    setForm((prev) => (prev ? { ...prev, id: saved.id } : prev));
    load();
  };

  const remove = async (o: LaunchOptionRow) => {
    if (!window.confirm(`Delete "${o.name}"? Groups that chose it on their sheet lose that choice.`)) return;
    const { error: delError } = await adminDeleteLaunchOption(email, o.id);
    if (delError) return setError(delError.message);
    if (form?.id === o.id) setForm(null);
    load();
  };

  const upload = async (e: React.ChangeEvent<HTMLInputElement>, kind: "image" | "video") => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !form) return;
    // The picture is named after the rocket (or the option, if no rocket is given).
    const vehicleSlug = slugify(form.vehicle) || slugify(form.name);
    if (kind === "image" && !vehicleSlug) return setError("Enter the rocket's name before uploading its picture.");
    if (kind === "video" && !form.id) return setError("Save the launch option once before uploading its video.");

    setUploading(kind);
    setError(null);
    const { data: url, error: uploadError } =
      kind === "image" ? await uploadLaunchOptionImage(vehicleSlug, file) : await uploadLaunchVideo(form.id!, file);
    setUploading(null);
    if (uploadError) return setError(uploadError.message);
    setForm((prev) => (prev ? { ...prev, [kind === "image" ? "imageUrl" : "videoUrl"]: url ?? "" } : prev));
  };

  const field = (label: string, key: keyof LaunchForm, extra: object = {}) => (
    <TextField
      label={label}
      size="small"
      value={form ? (form[key] as string) : ""}
      onChange={(e) => setForm((prev) => (prev ? { ...prev, [key]: e.target.value } : prev))}
      sx={{ ...fieldSx, "& .MuiFormHelperText-root": { color: "rgba(226,232,240,0.5)", mx: 0.25 } }}
      {...extra}
    />
  );

  return (
    <AdminShell title="Launches" currentPage="AdminLaunches" handlePageChange={handlePageChange}>
      {error && (
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 13.5, lineHeight: 1.6 }}>
        The ways to orbit that groups choose from on their mission sheet; offer about three per mission. The orbit an option
        delivers to becomes the group's TLE after its launch, and its video plays when the countdown reaches zero.
      </Typography>

      {!form && (
        <Button
          onClick={() => setForm({ ...EMPTY_FORM, orderIndex: options.length })}
          startIcon={<AddRoundedIcon />}
          sx={primaryButtonSx({ alignSelf: "flex-start", color: "#FFFFFF" })}
        >
          Add launch option
        </Button>
      )}

      {form && (
        <Box sx={panelSx}>
          <Stack spacing={1.75}>
            <Typography sx={sectionTitleSx}>{form.id ? "Edit launch option" : "New launch option"}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1.25 }}>
              {field("Name (e.g. Transporter rideshare)", "name")}
              {field("Rocket (e.g. Falcon 9)", "vehicle")}
              {field("Provider (e.g. SpaceX)", "provider")}
            </Box>
            {field("Summary shown to the groups", "summary", { multiline: true, minRows: 2 })}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" }, gap: 1.25 }}>
              {field("Launch site", "launchSite")}
              {field("Site latitude (°)", "siteLat")}
              {field("Site longitude (°)", "siteLon")}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr 1fr" }, gap: 1.25 }}>
              {field("Orbit altitude (km)", "altitudeKm")}
              {field("Inclination (°)", "inclinationDeg")}
              {field("Eccentricity", "eccentricity")}
              {field("Price (free text)", "priceText")}
            </Box>
            <Typography sx={mutedTextSx}>
              The orbit is where the rocket releases the satellite. Latitude north and longitude east are positive.
            </Typography>

            <Stack spacing={1}>
              <Stack spacing={0.25}>
                <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 12, fontWeight: 700 }}>TLE details (optional)</Typography>
                <Typography sx={{ ...mutedTextSx, fontSize: 11.5 }}>
                  Used for the TLE each group receives after launching with this option. Leave a field empty and a realistic
                  value is picked: the orbit plane from the launch time and site, a catalog number around 68,000, small typical
                  drag values. Changes apply to launches from now on; groups already in orbit keep their TLE (reset their launch
                  or edit their TLE in Workshops).
                </Typography>
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1.25 }}>
                {field("Intl. designator (YYNNN)", "tleIntlDesignator", { placeholder: "26045", helperText: "Year + launch number" })}
                {field("First catalog number", "tleCatalogBase", { placeholder: "68120", helperText: "Groups get one just above" })}
                {field("RAAN (°)", "tleRaanDeg", { helperText: "Orbit plane; empty = from launch time" })}
                {field("Argument of perigee (°)", "tleArgPerigeeDeg")}
                {field("Mean anomaly (°)", "tleMeanAnomalyDeg", { helperText: "Position on the orbit at separation" })}
                {field("Drag term B*", "tleBstar", { placeholder: "0.0003" })}
                {field("Mean motion change (rev/day²)", "tleMeanMotionDot", { placeholder: "0.000015" })}
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    component="label"
                    size="small"
                    disabled={uploading !== null}
                    startIcon={uploading === "image" ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <UploadRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={uploadButtonSx}
                  >
                    {form.imageUrl ? "Replace picture" : "Upload picture"}
                    <input type="file" hidden accept="image/jpeg,image/jpg,image/png" onChange={(e) => upload(e, "image")} />
                  </Button>
                  {form.imageUrl && (
                    <Box component="img" src={form.imageUrl} alt="" sx={{ width: 40, height: 28, borderRadius: 1, objectFit: "cover" }} />
                  )}
                  {form.imageUrl && (
                    <Button size="small" onClick={() => setForm({ ...form, imageUrl: "" })} sx={textButtonSx}>
                      Remove
                    </Button>
                  )}
                </Stack>
                <Typography sx={{ ...mutedTextSx, fontSize: 11 }}>
                  JPEG or PNG, shown on the option's card on the mission sheet. Saved in the launches bucket as{" "}
                  {slugify(form.vehicle) || slugify(form.name) || "<rocket>"}.jpg (or .png).
                </Typography>
                {field("Picture credit (e.g. Photo: ISRO)", "imageCredit")}
              </Stack>
              <Stack spacing={0.75} sx={{ flex: 1.4 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" }, gap: 1.25 }}>
                  {field("Launch video: YouTube link", "youtubeUrl", { placeholder: "https://www.youtube.com/watch?v=…" })}
                  {field("Play from", "youtubeStart", { placeholder: "29:41" })}
                  {field("Orbit reached at (optional)", "youtubeEnd", { placeholder: "30:33" })}
                </Box>
                <Typography sx={{ ...mutedTextSx, fontSize: 11 }}>
                  The video plays beside the flight simulation from "Play from" (ideally liftoff) and keeps playing, so the group
                  can watch on. "Orbit reached at" is the moment in the video when the rocket reaches orbit: the simulation reaches
                  orbit after the same time
                  {(() => {
                    const start = parseClipTime(form.youtubeStart);
                    const end = parseClipTime(form.youtubeEnd);
                    return start !== null && end !== null && end > start ? ` (here ${end - start} s)` : "";
                  })()}
                  . Leave it empty for a standard {DEFAULT_POWERED_S} s.
                </Typography>
                {form.videoUrl && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ ...mutedTextSx, fontSize: 11 }}>
                      An uploaded video is also set; it's only used without a YouTube link.
                    </Typography>
                    <Button size="small" onClick={() => setForm({ ...form, videoUrl: "" })} sx={textButtonSx}>
                      Remove it
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Stack>

            <Stack spacing={0.25}>
              <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 12, fontWeight: 700 }}>Offered in these missions</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
                {missions.map((m) => (
                  <FormControlLabel
                    key={m.id}
                    sx={{ mx: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={form.missionIds.includes(m.id)}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            missionIds: e.target.checked ? [...form.missionIds, m.id] : form.missionIds.filter((id) => id !== m.id),
                          })
                        }
                        sx={{ color: "rgba(226,232,240,0.5)", "&.Mui-checked": { color: "#A78BFA" } }}
                      />
                    }
                    label={<Typography sx={{ color: "#FFFFFF", fontSize: 13 }}>{m.name}</Typography>}
                  />
                ))}
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setForm(null)} sx={textButtonSx}>
                Close
              </Button>
              <Button onClick={save} disabled={saving} sx={primaryButtonSx({ color: "#FFFFFF" })}>
                {saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Save"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress sx={{ color: "#C4B5FD" }} />
        </Stack>
      ) : (
        <Stack spacing={1.25}>
          {options.length === 0 && <Typography sx={mutedTextSx}>No launch options yet.</Typography>}
          {options.map((o) => {
            const offeredIn = missions.filter((m) => missionIdsOf(o.id).includes(m.id));
            return (
              <Box key={o.id} sx={panelSx}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1}>
                  {o.image_url && <Box component="img" src={o.image_url} alt="" sx={{ width: 64, height: 44, borderRadius: 1, objectFit: "cover" }} />}
                  <Stack sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 14 }}>
                      {o.name}{" "}
                      <Box component="span" sx={{ color: "#C4B5FD", fontWeight: 700 }}>
                        {[o.vehicle, o.provider].filter(Boolean).join(" · ")}
                      </Box>
                    </Typography>
                    <Typography sx={mutedTextSx}>
                      {Math.round(o.orbit_altitude_km)} km · {o.orbit_inclination_deg}° · {o.launch_site || "no launch site"}
                      {o.video_url ? " · video" : " · no video yet"}
                      {o.price_text ? "" : " · no price yet"}
                    </Typography>
                  </Stack>
                  {offeredIn.length === 0 ? (
                    <Chip label="Not offered in any mission" size="small" sx={{ bgcolor: "rgba(226,232,240,0.1)", color: "rgba(226,232,240,0.6)", fontSize: 11 }} />
                  ) : (
                    offeredIn.map((m) => (
                      <Chip key={m.id} label={m.name} size="small" sx={{ bgcolor: "rgba(168,85,247,0.18)", color: "#E9D5FF", fontSize: 11 }} />
                    ))
                  )}
                  <IconButton size="small" aria-label="Edit launch option" onClick={() => edit(o)}>
                    <EditRoundedIcon sx={{ fontSize: 17, color: "rgba(226,232,240,0.7)" }} />
                  </IconButton>
                  <IconButton size="small" aria-label="Delete launch option" onClick={() => remove(o)}>
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: "rgba(248,113,113,0.75)" }} />
                  </IconButton>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </AdminShell>
  );
}
